import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';

import { Alert } from '../models/Alert';
import { ExternalLink } from '../models/ExternalLink';
import { IFile } from '../models/IFile';
import { IGetListOfFilesResponse } from '../models/IGetListOfFilesResponse';
import { IGetRefinerResponse } from '../models/IGetRefinerResponse';
import { ILocation } from '../models/ILocation';
import { ISelectedRefinerItem } from '../models/ISelectedRefinerItem';
import { ISite } from '../models/ISite';
import { QuickReference } from '../models/QuickReference';
import Constants from './Constants';
import { IAuthSettings } from '../models/IAuthSettings';
import { AuthService } from './AuthService';

interface CsrfTokenResponse {
    token: string;
}

export class SharePointService {
    private static _instance: SharePointService;
    private _baseUrl = window.location.origin + '/api/WHCPortal';
    private _csrfToken: string | null = null;
    private _navigate: ((to: any, options?: any) => void) | null = null;
    private _authService = AuthService.Instance;

    public static get Instance() {
        return this._instance || (this._instance = new this());
    }

    // Set the navigate function from React Router
    public setNavigate(navigate: (to: any, options?: any) => void): void {
        this._authService.setExpirationTime();
        this._navigate = navigate;
    }

    // Updated handleError method - only handle non-403 errors
    private handleError(error: any, context: string): never {
        let errorMessage = 'An unexpected error occurred';

        if (error) {
            if (error.response) {
                errorMessage = error.response.data || error.response.statusText || `HTTP Error ${error.response.status}`;
            } else if (error.request) {
                errorMessage = 'No response received from server';
            } else if (error.message) {
                errorMessage = error.message;
            }
        } else if (error instanceof Error) {
            errorMessage = error.message;
        }

        const contextualMessage = `${context}: ${errorMessage}`;

        // For 403 errors, just re-throw the original error so it can be caught by the caller
        if (error?.response?.status === 403) {
            throw error; // Re-throw original error for 403s
        }

        // For other errors, navigate to error page
        if (this._navigate) {
            this._navigate('/error', {
                state: {
                    message: contextualMessage,
                    context: context,
                    timestamp: new Date().toISOString(),
                    statusCode: error?.response?.status,
                    statusText: error?.response?.statusText
                }
            });
        } else {
            console.error('Navigate function not set. Falling back to window.location');
            // Prevent infinite reload loop if an error occurs on the error page itself
            if (window.location.pathname !== '/error') {
                const errorPageUrl = `/error?message=${encodeURIComponent(contextualMessage)}`;
                window.location.href = errorPageUrl;
            }
        }

        throw new Error(contextualMessage);
    }

    // Updated helper methods to handle 403 errors gracefully
    private async makeProtectedRequest<T>(config: AxiosRequestConfig): Promise<AxiosResponse<any>> {
        this._authService.setExpirationTime();
        try {
            await this.ensureCsrfToken();

            const protectedConfig: AxiosRequestConfig = {
                ...config,
                withCredentials: true,
                headers: {
                    ...config.headers,
                    'X-CSRF-TOKEN': this._csrfToken == null ? "" : this._csrfToken
                },
            };

            return await axios(protectedConfig);
        } catch (error) {
            // Let 403 errors bubble up, handle others
            if ((error as any)?.response?.status === 403) {
                throw error; // Re-throw 403 errors so they can be caught by useQuery
            }
            this.handleError(error, `Protected request failed (${config.method?.toUpperCase()} ${config.url})`);
        }
    }

    private async makeGetRequest<T>(config: AxiosRequestConfig): Promise<AxiosResponse<any>> {
        this._authService.setExpirationTime();
        try {
            return await axios({
                ...config,
                withCredentials: true,
            });
        } catch (error) {
            // Let 403 errors bubble up, handle others
            if ((error as any)?.response?.status === 403) {
                throw error; // Re-throw 403 errors so they can be caught by useQuery
            }
            this.handleError(error, `GET request failed (${config.url})`);
        }
    }

    // Initialize CSRF token on first use
    private async ensureCsrfToken(): Promise<void> {
        if (!this._csrfToken) {
            try {
                const result: AxiosResponse<any> = await axios({
                    method: 'get',
                    url: `${this._baseUrl}/csrf-token`,
                    withCredentials: true,
                });
                this._csrfToken = result.data.token;
            } catch (error) {
                this.handleError(error, 'Failed to initialize CSRF token');
            }
        }
    }

    public async getRefiners(): Promise<IGetRefinerResponse[]> {
        const result: AxiosResponse<any> = await this.makeGetRequest({
            method: 'get',
            url: `${this._baseUrl}/GetRefiners`,
        });

        return result.data.map(
            (item: any) =>
            ({
                title: item.Title,
                property: item.Property,
                siteId: item.SiteId,
                locationId: item.LocationId,
            } as IGetRefinerResponse)
        );
    }

    public async getListOfFiles(
        locationId: number,
        siteId: number,
        keyword: string,
        rowLimit: number,
        startRow: number,
        selectedRefiners: Array<ISelectedRefinerItem>,
        refiners: Array<string>,
        useTemplate: boolean = false
    ): Promise<IGetListOfFilesResponse> {
        let data: any = {
            locationId,
            siteId,
            keyword,
            rowLimit,
            startRow,
            selectedRefiners,
            refiners,
            useTemplate
        };

        const result: AxiosResponse<any> = await this.makeProtectedRequest({
            method: 'post',
            url: `${this._baseUrl}/GetListOfFiles`,
            data,
        });

        // Replace {rootUrl} in preview URLs with the actual root URL
        return {
            ...result.data,
            files: result.data.files.map((item: any) => ({
                ...item,
                previewUrl:
                    item.previewUrl.indexOf('{rootUrl}') > -1
                        ? item.previewUrl.replace('{rootUrl}', window.location.origin)
                        : item.previewUrl,
            })),
        };
    }

    public async getLocations(): Promise<Array<ILocation>> {
        const result: AxiosResponse<any> = await this.makeGetRequest({
            method: 'get',
            url: `${this._baseUrl}/GetLocations`,
        });
        return result.data;
    }

    public async getSites(): Promise<Array<ISite>> {
        const result: AxiosResponse<any> = await this.makeGetRequest({
            method: 'get',
            url: `${this._baseUrl}/GetSites`,
        });
        return result.data;
    }

    public async getExternalLinks(): Promise<Array<ExternalLink>> {
        const result: AxiosResponse<any> = await this.makeGetRequest({
            method: 'get',
            url: `${this._baseUrl}/GetExternalLinks`,
        });
        return result.data.map(
            (item: any) =>
            ({
                title: item.Title,
                url: item.URL,
                target: item.Target,
                order: item.Order,
                state: item.State,
            } as ExternalLink)
        );
    }

    public async getQuickReferences(): Promise<Array<QuickReference>> {
        const result: AxiosResponse<any> = await this.makeGetRequest({
            method: 'get',
            url: `${this._baseUrl}/GetQuickReferences`,
        });
        return result.data.map(
            (item: any) =>
            ({
                id: item.Id,
                title: item.Title,
                url: item.URL,
                previewUrl: Constants.QUICK_REFERENCE_URL + item.Id,
                order: item.Order,
                target: item.Target,
                fileType: item.URL.split('.').pop()?.toLowerCase() || '',
            } as QuickReference)
        );
    }

    public async getAlerts(): Promise<Array<Alert>> {
        const result: AxiosResponse<any> = await this.makeGetRequest({
            method: 'get',
            url: `${this._baseUrl}/GetAlerts`,
        });
        return result.data.map(
            (item: any) =>
            ({
                id: item.Id,
                title: item.Title,
                description: item.Description,
                alertType: item.AlertType,
            } as Alert)
        );
    }

    public async getRecentDocuments(state: string): Promise<Array<IFile>> {
        const result: AxiosResponse<any> = await this.makeGetRequest({
            method: 'get',
            url: `${this._baseUrl}/GetRecentDocuments?state=${state}`,
        });

        return result.data;
    }

    // Method to refresh CSRF token if needed
    public async refreshCsrfToken(): Promise<void> {
        try {
            this._csrfToken = null;
            await this.ensureCsrfToken();
        } catch (error) {
            this.handleError(error, 'Failed to refresh CSRF token');
        }
    }

    // Method to manually set CSRF token (useful for testing or special cases)
    public setCsrfToken(token: string): void {
        this._csrfToken = token;
    }

    public async getAuthSettings(): Promise<IAuthSettings> {
        const result: AxiosResponse<any> = await this.makeGetRequest({
            method: 'get',
            url: `${this._baseUrl}/GetAuthSettings`,
        });
        return result.data;
    }
}